import React from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  Collapse,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  useDisclosure,
} from "@chakra-ui/react";
import moment from "moment";
import { appImages } from "../assets/appicons";

// ===== Tông màu khớp mobile-v2 =====
export const PRIMARY = "#4081EB";
export const BORDER = "#E1E1E1";
export const TEXT = "#08142D";
export const MUTED = "#94A3B8";

// Chuẩn hoá status (số 1/2/3 hoặc chuỗi) -> nhãn IN HOA + màu khớp app
export const requestStatusUi = (status) => {
  const s = typeof status === "string" ? status.toUpperCase() : status;
  const approved = s === 2 || s === "APPROVED";
  const rejected = s === 3 || s === "REJECTED";
  if (approved) return { label: "ĐÃ DUYỆT", bg: "#E9FFF4", color: "#0BA859" };
  if (rejected) return { label: "TỪ CHỐI", bg: "#FFF1F0", color: "#E5484D" };
  return { label: "ĐANG CHỜ DUYỆT", bg: "#FFF7E8", color: "#F79D0D" };
};

export const StatusPill = ({ status }) => {
  const ui = requestStatusUi(status);
  return (
    <Box display="inline-block" bg={ui.bg} color={ui.color} fontSize="12px" fontWeight={700} px="8px" py="3px" borderRadius="6px" whiteSpace="nowrap">
      {ui.label}
    </Box>
  );
};

// Ô field trong bộ lọc (label nhỏ phía trên + hộp xám khớp app)
const FilterField = ({ label, children }) => (
  <Box flex="1" minW="0">
    <Text fontSize="13px" color={TEXT} mb="6px">{label}</Text>
    {children}
  </Box>
);

// Hộp field kiểu app: nền xám, bo 8, giá trị + chevron
const FieldBox = ({ children, onClick }) => (
  <Flex
    as={onClick ? "button" : "div"}
    type={onClick ? "button" : undefined}
    onClick={onClick}
    align="center"
    justify="space-between"
    bg="#F7F9FC"
    borderRadius="8px"
    h="40px"
    px="10px"
    w="100%"
  >
    {children}
  </Flex>
);

// Ô chọn ngày kiểu app (hiển thị DD/MM/YYYY) - input date native ẩn để mở lịch
const DateField = ({ value, onChange }) => (
  <Box position="relative" w="100%">
    <FieldBox>
      <Text fontSize="14px" color={value ? TEXT : MUTED}>
        {value ? moment(value).format("DD/MM/YYYY") : "dd/mm/yyyy"}
      </Text>
      <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
    </FieldBox>
    <Input
      type="date"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      position="absolute"
      inset="0"
      w="100%"
      h="100%"
      opacity={0}
      p="0"
      cursor="pointer"
    />
  </Box>
);

// Bộ lọc chi tiết (thu gọn/mở rộng) khớp app
export const FilterCard = ({
  open,
  onToggle,
  status,
  onStatusChange,
  fromDate,
  toDate,
  onFromChange,
  onToChange,
  statusOptions = [
    { value: "ALL", label: "Tất cả" },
    { value: "PENDING", label: "Chờ duyệt" },
    { value: "APPROVED", label: "Đã duyệt" },
    { value: "REJECTED", label: "Từ chối" },
  ],
}) => {
  const sheet = useDisclosure();
  const statusLabel = statusOptions.find((o) => o.value === status)?.label || "Tất cả";

  return (
    <Box>
      <Flex
        as="button"
        w="100%"
        h="44px"
        px="12px"
        align="center"
        justify="space-between"
        bg="white"
        border="1px solid"
        borderColor={BORDER}
        borderBottomWidth={open ? "0" : "1px"}
        borderTopRadius="12px"
        borderBottomRadius={open ? "0" : "12px"}
        onClick={onToggle}
      >
        <Flex align="center" gap="8px">
          <img src={appImages.icon_filter_detail} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
          <Text fontSize="14px" fontWeight={600} color={TEXT}>Bộ lọc chi tiết</Text>
        </Flex>
        <img
          src={open ? appImages.icon_chevron_up : appImages.icon_chevron_down}
          alt=""
          width={18}
          height={18}
          style={{ objectFit: "contain" }}
        />
      </Flex>
      <Collapse in={open} animateOpacity>
        <Box bg="white" border="1px solid" borderColor={BORDER} borderTop="0" borderBottomRadius="12px" p="12px">
          <FilterField label="Trạng thái">
            <FieldBox onClick={sheet.onOpen}>
              <Text fontSize="14px" color={TEXT}>{statusLabel}</Text>
              <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
            </FieldBox>
          </FilterField>
          <Flex gap="10px" mt="12px">
            <FilterField label="Từ ngày">
              <DateField value={fromDate} onChange={onFromChange} />
            </FilterField>
            <FilterField label="Đến ngày">
              <DateField value={toDate} onChange={onToChange} />
            </FilterField>
          </Flex>
        </Box>
      </Collapse>

      {/* Bottom sheet chọn trạng thái (khớp app) */}
      <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
        <DrawerOverlay bg="rgba(0,0,0,0.5)" />
        <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
          <DrawerBody py="16px" px="16px">
            <Text fontSize="16px" fontWeight={700} color={TEXT} mb="4px">Trạng thái</Text>
            {statusOptions.map((o, i) => {
              const active = o.value === status;
              const last = i === statusOptions.length - 1;
              return (
                <Flex
                  key={o.value}
                  as="button"
                  w="100%"
                  align="center"
                  justify="space-between"
                  py="12px"
                  borderBottom={last ? "none" : "1px solid #EEF1F6"}
                  onClick={() => {
                    onStatusChange(o.value);
                    sheet.onClose();
                  }}
                >
                  <Text fontSize="15px" color={active ? PRIMARY : TEXT} fontWeight={active ? 600 : 400}>
                    {o.label}
                  </Text>
                  {active && (
                    <img src={appImages.icon_check} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
                  )}
                </Flex>
              );
            })}
            <Flex
              as="button"
              onClick={sheet.onClose}
              mt="12px"
              w="100%"
              h="46px"
              align="center"
              justify="center"
              borderRadius="10px"
              bg={PRIMARY}
              color="white"
              fontWeight={700}
            >
              Đóng
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

// Thẻ tổng số
export const SummaryCard = ({ label, total }) => (
  <Box bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px" mt="12px">
    <Text fontSize="13px" color={MUTED} fontWeight={500}>{label}</Text>
    <Text fontSize="22px" fontWeight={800} color={PRIMARY} mt="8px">{total || 0}</Text>
  </Box>
);

// Hàng tiêu đề danh sách + nút sắp xếp
export const ListHeaderRow = ({ title, sortLabel, onToggleSort }) => (
  <Flex align="center" justify="space-between" mt="16px" mb="14px">
    <Text fontSize="14px" fontWeight={600} color={TEXT}>{title}</Text>
    <Flex as="button" align="center" gap="6px" onClick={onToggleSort} color={TEXT}>
      <Text fontSize="14px" fontWeight={600}>{sortLabel}</Text>
      <img src={appImages.ion_sort_desc} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
    </Flex>
  </Flex>
);

// Nút tạo mới nổi góc dưới phải (icon_create khớp app)
export const Fab = ({ onClick }) => (
  <Box
    as="button"
    onClick={onClick}
    position="fixed"
    right={{ base: "20px", md: "calc(50% - 220px)" }}
    bottom="24px"
    zIndex={15}
    aria-label="Tạo mới"
  >
    <img src={appImages.icon_create} alt="Tạo mới" width={64} height={64} style={{ display: "block" }} />
  </Box>
);

// Dòng key-value trong card danh sách (label trái mờ, value phải đậm)
export const CardKV = ({ label, value }) => (
  <Flex gap="12px" align="flex-start">
    <Text fontSize="14px" color={MUTED} flex="0 0 42%">{label}</Text>
    <Text fontSize="14px" fontWeight={600} color={TEXT} flex="1" textAlign="right" whiteSpace="pre-line">
      {value ?? "-"}
    </Text>
  </Flex>
);

// Dòng thông tin trong màn chi tiết (có gạch dưới)
export const PropCell = ({ label, value, borderBottom = true }) => (
  <Flex justify="space-between" align="flex-start" gap="12px" py="18px" borderBottom={borderBottom ? "0.5px solid" : "none"} borderColor={BORDER}>
    <Text fontSize="14px" color="#51607B" flex="0 0 42%">{label}</Text>
    <Text fontSize="14px" fontWeight={600} color={TEXT} flex="1" textAlign="right" whiteSpace="pre-line">
      {value !== undefined && value !== null && value !== "" ? String(value) : "-"}
    </Text>
  </Flex>
);
